import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import type { Project, UpdateProject } from '../../../lib/db/models'
import { getScrollContainer } from '@/src/renderer/lib/dom/getScrollContainer'
import { titleHtmlFromPlain } from '../components/ProjectTitle/ProjectTitleEditor'
import { titleHasFormatting } from '../components/ProjectTitle/titleFormat'
import { rememberUndo, touchUndoRegion } from '@/src/renderer/lib/undo'

interface UseProjectHeaderParams {
  project: Project | null | undefined
  updateProject: (updates: Partial<UpdateProject>) => Promise<void>
  projectId: string | undefined
  activeTab: { id: string } | null | undefined
  updateTab: (id: string, data: { title: string }) => void
  closeActiveTab: () => void
  setBreadcrumbTitle: (title: string | null) => void
  navFocusFirstRef: React.RefObject<(() => void) | null>
  t: TFunction
  /** True when the owning ProjectPage is the active tab. Inactive pages
   *  stay mounted under `display: none`; the breadcrumb observer must
   *  stay silent for them or the project title leaks into every tab's
   *  breadcrumb. */
  isActive?: boolean
}

/**
 * Welcher Titel in der Breadcrumb steht, wenn der Beobachter sich meldet: der Projektname, sobald
 * der Titel aus dem Bild gescrollt ist — sonst keiner.
 *
 * **Nur ein Titel, der noch dasteht, kann weggescrollt sein** (gemeldet 30.09.2026). Beim
 * Projektwechsel zeigt `ProjectPage` einen Platzhalter ohne Titel; das alte Titelfeld fliegt aus
 * dem DOM, der Beobachter meldet „nicht sichtbar“ — und die Breadcrumb zeigte kurz den alten
 * Namen, obwohl niemand gescrollt hatte. Dasselbe gilt für ein verstecktes Feld (Größe 0).
 */
export function breadcrumbTitleFor(entry: IntersectionObserverEntry, name: string): string | null {
  const scrolledAway = !entry.isIntersecting && entry.target.isConnected && entry.boundingClientRect.height > 0
  return scrolledAway && name ? name : null
}

export function useProjectHeader({
  project,
  updateProject,
  projectId,
  activeTab,
  updateTab,
  closeActiveTab,
  setBreadcrumbTitle,
  navFocusFirstRef,
  t,
  isActive = true,
}: UseProjectHeaderParams) {
  const [name, setName] = useState('')
  const [savedName, setSavedName] = useState('')
  // Der gestaltete Titel (ADR 0030) — `name` ist sein Klartext.
  const [nameHtml, setNameHtml] = useState('<p></p>')
  const [savedNameHtml, setSavedNameHtml] = useState('<p></p>')
  const [notesValue, setNotesValue] = useState('')
  const [savedNotes, setSavedNotes] = useState('')
  const [isEditingHeader, setIsEditingHeader] = useState(false)

  // Das Schreibfeld des Titels (ProseMirror, `ProjectTitleEditor`).
  const titleRef = useRef<HTMLElement>(null)
  const notesRef = useRef<HTMLTextAreaElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const hasInitialized = useRef(false)
  const prevProjectIdRef = useRef(projectId)

  // Detect projectId change — only reset the init flag so the new project's
  // data gets picked up by the useLayoutEffect below. DON'T clear name/notes
  // here: the old values must stay in the DOM so the crossfade ghost (created
  // in ProjectPage's useLayoutEffect) captures the correct old content.
  // The real page is hidden (opacity: 0) anyway, so stale values are invisible.
  if (projectId !== prevProjectIdRef.current) {
    prevProjectIdRef.current = projectId
    hasInitialized.current = false
    if (isEditingHeader) setIsEditingHeader(false)
  }

  // Initialize name + notes from project data.
  // Guard against stale useLiveQuery results: only init when project.id matches projectId.
  // useLayoutEffect so the title is set BEFORE the page fade-in (which also uses useLayoutEffect).
  useLayoutEffect(() => {
    if (project && !hasInitialized.current && project.id === projectId) {
      setName(project.name)
      setSavedName(project.name)
      const html = project.nameHtml ?? titleHtmlFromPlain(project.name)
      setNameHtml(html)
      setSavedNameHtml(html)
      setNotesValue(project.description ?? '')
      setSavedNotes(project.description ?? '')
      hasInitialized.current = true

      // Ensure tab shows default name for unnamed projects
      if (!project.name && activeTab) {
        updateTab(activeTab.id, { title: t('project.defaultName') })
      }

      // Auto-focus if project has no name (just created)
      if (!project.name) {
        const focusTimer = setTimeout(() => titleRef.current?.focus(), 0)
        return () => clearTimeout(focusTimer)
      }
    }
    return undefined
  // Weitere Abhängigkeiten liefen den Aufräumer an und brächen den Fokus-Timer ab;
  // hasInitialized verhindert, dass er neu gesetzt wird.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, projectId])

  // Close tab when project is deleted (e.g. from another device via sync).
  useEffect(() => {
    if (project?.deletedAt) {
      queueMicrotask(() => closeActiveTab())
    }
  }, [project?.deletedAt, closeActiveTab])

  // Sync external name changes (e.g. from another device) into local state and tab title
  useEffect(() => {
    if (!project || !hasInitialized.current) return
    const remoteHtml = project.nameHtml ?? titleHtmlFromPlain(project.name)
    if (project.name === savedName && remoteHtml === savedNameHtml) return

    if (document.activeElement !== titleRef.current) {
      setName(project.name)
      setSavedName(project.name)
      setNameHtml(remoteHtml)
      setSavedNameHtml(remoteHtml)

      if (activeTab) {
        updateTab(activeTab.id, { title: project.name || t('project.defaultName') })
      }
    }
  // Nur auf Änderungen von außen reagieren, nicht auf eigene Speicherstände.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name, project?.nameHtml])

  // Sync external description changes into local notes state
  useEffect(() => {
    if (!project || !hasInitialized.current) return
    const remote = project.description ?? ''
    if (remote === savedNotes) return

    if (document.activeElement !== notesRef.current) {
      setNotesValue(remote)
      setSavedNotes(remote)
    }
  // Nur auf Änderungen von außen reagieren, nicht auf eigene Speicherstände.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.description])

  // Auto-resize textareas when value changes externally (init, sync, revert).
  // Bail while the element is hidden (`offsetParent === null`, e.g. the header
  // is `display: none` in list mode) — measuring there yields `scrollHeight: 0`
  // and would collapse the textarea to 0px, leaving the title invisible after
  // returning to full mode. We deliberately do NOT re-measure on the list→full
  // switch: the window is still at list width when the effect would run, so the
  // title would wrap and lock in a too-tall height. The textarea keeps its last
  // full-mode height (or the natural rows=1 height), which is correct.

  useEffect(() => {
    const el = notesRef.current
    if (!el || el.offsetParent === null) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [notesValue, isEditingHeader])

  // Auch bei BREITENÄNDERUNG neu messen, nicht nur wenn sich der Text ändert.
  // Wird das Fenster schmaler oder klappt der Projekt-Canvas auf, umbricht der
  // Titel in mehr Zeilen — die einmal gesetzte Höhe blieb aber stehen und
  // `overflow: hidden` schnitt die letzte Zeile ab.
  //
  // Ein ResizeObserver misst NACH dem Umbruch, also bei der endgültigen Breite.
  // Damit greift auch der Vorbehalt oben nicht, beim Wechsel Liste→Voll noch mit
  // Listenbreite zu messen. Dieselbe Absicherung wie dort: nichts anfassen,
  // solange das Element verborgen ist (`offsetParent === null`, Breite 0) —
  // sonst kollabiert die Höhe auf 0.
  //
  // Angehängt wird, SOBALD das Titelfeld da ist — nicht einmal beim Einhängen
  // des Hooks. `ProjectPage` rendert zuerst die Lade-Hülle ohne Titelfeld; ein
  // Effekt mit `[]` fand dort `null` und lief nie wieder. Dann blieb der Titel
  // nach dem Breiterziehen 448px hoch (oder beim Schmalziehen eine Zeile hoch,
  // während der Text buchstabenweise umbrach). Deshalb ohne Abhängigkeiten, mit
  // Vergleich gegen das zuletzt beobachtete Element. Die Notizen erscheinen noch
  // später (Fokus, gespeicherte Notizen) — sie werden beim Feuern gelesen, nicht
  // beim Anhängen. Beide reichen bis an die Kante der Liste (#296); Fenster und
  // Canvas ändern beide Breiten zugleich, das Titelfeld reicht als Signal. (Die
  // Notizen selbst zu beobachten ginge nicht: Ihr Höhe-Setzen meldete sich sofort
  // wieder — „ResizeObserver loop“.)
  const observedTitleRef = useRef<HTMLElement | null>(null)
  const titleResizeObserverRef = useRef<ResizeObserver | null>(null)

  useEffect(() => {
    const el = titleRef.current
    if (el === observedTitleRef.current) return
    titleResizeObserverRef.current?.disconnect()
    titleResizeObserverRef.current = null
    observedTitleRef.current = el
    if (!el) return

    // Der Titel wächst als Editor von selbst mit; nur die Notizen sind ein `<textarea>`.
    const fit = (node: HTMLElement | null) => {
      if (!(node instanceof HTMLTextAreaElement) || node.offsetParent === null || node.clientWidth === 0) return
      node.style.height = 'auto'
      node.style.height = `${node.scrollHeight}px`
    }

    const observer = new ResizeObserver(() => {
      fit(notesRef.current)
    })
    observer.observe(el)
    titleResizeObserverRef.current = observer
  })

  useEffect(() => () => {
    titleResizeObserverRef.current?.disconnect()
    titleResizeObserverRef.current = null
    // Sonst hängte der Effekt oben nach dem StrictMode-Probelauf nicht neu an.
    observedTitleRef.current = null
  }, [])

  // Show project title in breadcrumb when it scrolls out of view.
  // Read savedName via ref to keep the observer stable across title changes.
  const savedNameRef = useRef(savedName)
  savedNameRef.current = savedName
  // Nach einem Wechsel steht das neue Titelfeld erst, wenn das neue Projekt geladen ist — auch
  // bei gleichem Namen muss der Beobachter dann an das neue Feld.
  const loadedProjectId = project?.id ?? null

  useEffect(() => {
    // Inactive tabs stay mounted via TabStack (display: none). Their title
    // element registers as "not intersecting" to an IntersectionObserver —
    // which would falsely claim the project's name should be shown in the
    // breadcrumb, leaking the project title into every other tab's chrome.
    // Only run the observer while this ProjectPage is the active tab.
    if (!isActive) {
      setBreadcrumbTitle(null)
      return
    }
    const titleEl = titleRef.current
    if (!titleEl) return

    const scrollContainer = getScrollContainer(titleEl)
    if (!scrollContainer) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setBreadcrumbTitle(breadcrumbTitleFor(entry, savedNameRef.current))
      },
      { root: scrollContainer, threshold: 0, rootMargin: '-16px 0px 0px 0px' },
    )

    observer.observe(titleEl)
    return () => {
      observer.disconnect()
      // When the page deactivates we might still be leaving behind the
      // project title we last pushed; clear it so the next active tab
      // starts from a blank breadcrumb.
      setBreadcrumbTitle(null)
    }
  }, [isActive, savedName, loadedProjectId, setBreadcrumbTitle])

  // Clean up breadcrumb title on unmount
  useEffect(() => {
    return () => setBreadcrumbTitle(null)
  }, [setBreadcrumbTitle])

  /**
   * **Titel und Icon sind rücknehmbar** (gewünscht 2026-09-26): Jede
   * gespeicherte Änderung merkt sich den Stand davor als die eine
   * **gemerkte Handlung** (ADR 0028) — `⌘Z` außerhalb des Titels holt ihn
   * zurück. Nach dem eigenen Schreiben aufrufen, sonst vergäße das
   * Schreiben die eben gemerkte Handlung wieder.
   */
  const rememberHeaderUndo = useCallback((before: Project, message: string) => {
    const restore: Partial<UpdateProject> = {
      name: before.name,
      nameHtml: before.nameHtml ?? null,
      icon: before.icon ?? null,
      iconColor: before.iconColor ?? null,
    }
    rememberUndo(() => updateProject(restore), message)
    touchUndoRegion('list')
  }, [updateProject])

  const handleIconChange = useCallback(async (next: { icon: string; iconColor: string | null } | null) => {
    if (!project) return
    const before = project
    await updateProject(next ? { icon: next.icon, iconColor: next.iconColor } : { icon: null, iconColor: null })
    rememberHeaderUndo(before, t('undo.restored.projectIcon'))
  }, [project, updateProject, rememberHeaderUndo, t])

  const handleSave = useCallback(async () => {
    const trimmed = name.trim()
    if (trimmed === savedName && nameHtml === savedNameHtml) return

    // Empty input: revert to saved name instead of committing default
    if (!trimmed) {
      setName(savedName)
      setNameHtml(savedNameHtml)
      if (activeTab) {
        updateTab(activeTab.id, { title: savedName || t('project.defaultName') })
      }
      return
    }

    // Ohne Gestaltung kein HTML: `name` genügt, und alles, was nur `name` liest, bleibt gleich.
    const html = titleHasFormatting(nameHtml) ? nameHtml : null
    const before = project
    await updateProject({ name: trimmed, nameHtml: html })
    if (before) rememberHeaderUndo(before, t('undo.restored.projectTitle'))
    setName(trimmed)
    setSavedName(trimmed)
    setSavedNameHtml(html ?? titleHtmlFromPlain(trimmed))

    // Update the tab title directly via context
    if (activeTab) {
      updateTab(activeTab.id, { title: trimmed })
    }

    // Trigger immediate sync after rename
  }, [name, savedName, nameHtml, savedNameHtml, updateProject, t, activeTab, updateTab, project, rememberHeaderUndo])

  const handleNotesSave = useCallback(async () => {
    const trimmed = notesValue.trim()
    if (trimmed === savedNotes) return

    await updateProject({ description: trimmed || null })
    setNotesValue(trimmed)
    setSavedNotes(trimmed)
  }, [notesValue, savedNotes, updateProject])

  const handleRevert = useCallback(() => {
    titleRef.current?.blur()
    notesRef.current?.blur()
    setName(savedName)
    setNameHtml(savedNameHtml)
    setNotesValue(savedNotes)
    setIsEditingHeader(false)
  }, [savedName, savedNameHtml, savedNotes])

  /**
   * Eine Formatierung aus dem Titelwerkzeug: sofort speichern, ohne dass das
   * Schreibfeld den Fokus hätte (gemeldet 2026-09-26). Ein leerer Titel wird
   * nicht gespeichert — wie beim Blur.
   */
  const handleTitleCommit = useCallback(async (html: string, plain: string) => {
    setNameHtml(html)
    setName(plain)
    const trimmed = plain.trim()
    if (!trimmed) return
    const stored = titleHasFormatting(html) ? html : null
    const before = project
    await updateProject({ name: trimmed, nameHtml: stored })
    if (before) rememberHeaderUndo(before, t('undo.restored.projectTitle'))
    setSavedName(trimmed)
    setSavedNameHtml(stored ?? titleHtmlFromPlain(trimmed))
    if (activeTab && trimmed !== savedName) updateTab(activeTab.id, { title: trimmed })
  }, [updateProject, activeTab, updateTab, savedName, project, rememberHeaderUndo, t])

  /** Enter im Titel: speichern und loslassen (wie vorher im `<textarea>`). */
  const handleTitleSubmit = useCallback(() => {
    handleSave()
    handleNotesSave()
    setIsEditingHeader(false)
    titleRef.current?.blur()
    notesRef.current?.blur()
  }, [handleSave, handleNotesSave])

  /** ↓ am Ende des Titels: in die Notizen, sonst in die Liste. */
  const handleTitleArrowDown = useCallback(() => {
    if (notesRef.current) {
      notesRef.current.focus()
    } else {
      titleRef.current?.blur()
      navFocusFirstRef.current?.()
    }
  }, [navFocusFirstRef])

  // Blur handler for the header container — tracks focus within title+notes
  const handleHeaderBlur = useCallback((e: React.FocusEvent) => {
    if (headerRef.current?.contains(e.relatedTarget as Node)) return
    handleSave()
    handleNotesSave()
    setIsEditingHeader(false)
  }, [handleSave, handleNotesSave])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
      handleNotesSave()
      setIsEditingHeader(false)
      titleRef.current?.blur()
      notesRef.current?.blur()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      handleRevert()
    }
  }, [handleSave, handleNotesSave, handleRevert])

  const handleNotesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
      handleNotesSave()
      setIsEditingHeader(false)
      titleRef.current?.blur()
      notesRef.current?.blur()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      handleRevert()
    }
    if (e.key === 'ArrowUp' && notesRef.current?.selectionStart === 0) {
      e.preventDefault()
      titleRef.current?.focus()
    }
    if (e.key === 'ArrowDown' && notesRef.current?.selectionStart === notesRef.current?.value.length) {
      e.preventDefault()
      notesRef.current?.blur()
      navFocusFirstRef.current?.()
    }
  }, [handleSave, handleNotesSave, handleRevert, navFocusFirstRef])

  return {
    name,
    setName,
    nameHtml,
    setNameHtml,
    savedName,
    notesValue,
    setNotesValue,
    savedNotes,
    isEditingHeader,
    setIsEditingHeader,
    titleRef,
    notesRef,
    headerRef,
    handleKeyDown,
    handleNotesKeyDown,
    handleHeaderBlur,
    handleTitleSubmit,
    handleTitleCommit,
    handleIconChange,
    handleTitleArrowDown,
    handleRevert,
  }
}
